/**
 * Registers the glovebox service worker.
 *
 * Only runs for production builds — a service worker in front of Vite's dev
 * server fights with HMR. `onUpdateReady` fires when a newer version has been
 * downloaded and is waiting to take over.
 */
// Set only by applyUpdate(), so the controllerchange handler can tell a
// user-accepted update apart from the initial clients.claim().
let updateAccepted = false;

export const registerServiceWorker = ({ onUpdateReady } = {}) => {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');

      // A worker is already waiting from a previous visit.
      if (registration.waiting) onUpdateReady?.(registration);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          // `controller` is null on the very first install — that's not an
          // update, it's the initial cache warm-up.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            onUpdateReady?.(registration);
          }
        });
      });
    } catch (err) {
      // A failed registration must never break the app.
      console.warn('[pwa] service worker registration failed:', err);
    }
  });

  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Reload ONLY when the client accepted an update via applyUpdate().
    // controllerchange also fires when the very first worker calls
    // clients.claim(), and reloading there yanks the page out from under
    // someone mid-form for no reason.
    if (!updateAccepted) return;
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
};

/**
 * Asks the waiting worker to take over. Returns false when there is nothing
 * waiting -- the caller must not sit on a spinner in that case, because no
 * controllerchange (and so no reload) is coming.
 */
export const applyUpdate = (registration) => {
  if (!registration?.waiting) return false;
  updateAccepted = true;
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
};
